import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';

import { DialogControls } from '../../../components/DialogControls/DialogControls';
import { useHideModal } from '../../../actions/modal';
import { onCheckFuelInfo, saveHoldData } from '../../../actions/navigation';
import { NavType } from '../../../components/NavigationRoutes/constants';
import { clonePoint, convertFieldsValidator, getDeclarationFieldName, getFieldLabel, getFields, getFieldType, PointFields } from '../tools';
import { useStyles } from './useStyles';
import { useMobileWidth } from '../../../components/hooks/useMobileWidth';
import { useForm } from '../../../components/hooks/useForm/useForm';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '../../../components/material-ui/Dialog';
import { FormControl } from '../../../components/material-ui/FormControl';
import { TextField } from '../../../components/material-ui/TextField';
import { Typography } from '../../../components/material-ui/Typography';
import { DateTimePicker } from '../../../components/DateTimePicker/DateTimePicker';
import { useColumnsDeclaration } from '../../../selectors/routesDeclaration';
import { useSingleConvert } from '../../../components/hooks/useMeasureConverter';

const fieldList = [PointFields.START_HOLD, PointFields.END_HOLD, PointFields.COMMENT, PointFields.ACT];
const convertedFields = [PointFields.ACT];

export const Hold = React.forwardRef((props, ref) => {
    const { isNewPoint = false, pos, point } = props;
    const columnsDeclaration = useColumnsDeclaration();
    const classes = useStyles();
    const { isMobilePortrait } = useMobileWidth();
    const hideModal = useHideModal();
    const dispatch = useDispatch();
    const { t: translate } = useTranslation();
    const handleSuccess = React.useCallback(
        (data) => {
            dispatch(saveHoldData(pos, data, isNewPoint));
            dispatch(onCheckFuelInfo());
            hideModal();
        },
        [pos, isNewPoint]
    );

    const fields = React.useMemo(() => getFields(fieldList), []);
    const convert = useSingleConvert();
    React.useMemo(() => convertFieldsValidator(convertedFields, fields, convert), []);

    const { inputs, handleInputChange, handleSubmit, status } = useForm({
        onSuccess: handleSuccess,
        initialData: {
            ...clonePoint(fieldList, point),
            ...(isNewPoint && {
                pointNumber: point.pointNumber,
                ato: null,
                dist: null,
                isUserAdd: 1,
                comment: '',
            }),
            type: NavType.HOLD,
            dateCreate: point.dateCreate || isNewPoint ? moment.utc(point.dateCreate) : null,
        },
        fields,
    });

    const disableSubmit = Boolean(
        fieldList.find((key) => Object.values(status || {}).some((item) => item) || (fields[key].required && !inputs[key]))
    );
    const actions = React.useMemo(
        () => [
            { label: translate('cancel').toUpperCase(), onClick: hideModal },
            { label: translate('ok'), color: 'primary', type: 'submit', disabled: disableSubmit },
        ],
        [disableSubmit]
    );
    const handleChangeField = React.useCallback((name, value) => {
        handleInputChange({ target: { name, value } });
    }, []);

    return (
        <Dialog open={true} ref={ref} maxWidth="md" fullScreen={isMobilePortrait}>
            <DialogTitle className={classes.title}>
                {translate(isNewPoint ? 'navigationRoute.addHold' : 'navigationRoute.editHold')}
            </DialogTitle>
            <form className={classes.fields} onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <div className={classes.dates}>
                        <DateTimePicker
                            name={PointFields.START_HOLD}
                            canBeEmpty={true}
                            value={get(inputs, PointFields.START_HOLD)}
                            labelTextDate={getFieldLabel(PointFields.START_HOLD)}
                            onChange={handleChangeField}
                            className={classes.picker}
                        />
                        <DateTimePicker
                            name={PointFields.END_HOLD}
                            canBeEmpty={true}
                            value={get(inputs, PointFields.END_HOLD)}
                            labelTextDate={getFieldLabel(PointFields.END_HOLD)}
                            onChange={handleChangeField}
                            className={classes.picker}
                        />
                    </div>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name={PointFields.ACT}
                            label={getDeclarationFieldName(PointFields.ACT, columnsDeclaration)}
                            placeholder={getDeclarationFieldName(PointFields.ACT, columnsDeclaration)}
                            onChange={handleInputChange}
                            helperText={status[PointFields.ACT]}
                            error={Boolean(status[PointFields.ACT])}
                            type={getFieldType(PointFields.ACT)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, PointFields.ACT, '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name={PointFields.COMMENT}
                            label={getFieldLabel(PointFields.COMMENT)}
                            onChange={handleInputChange}
                            fullWidth={true}
                            multiline={true}
                            value={get(inputs, PointFields.COMMENT, '')}
                            minRows={1}
                            maxRows={5}
                            className={classes.textarea}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <Typography color="error">{status.message}</Typography>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </form>
        </Dialog>
    );
});
